<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Call Schedule</h1>
        <p class="text-gray-500 mt-1">Manage and schedule calls with your elderly loved ones</p>
      </div>
      <div class="flex space-x-3">
        <button
          @click="showBulkSchedule = true"
          class="btn btn-secondary flex items-center space-x-2"
        >
          <CalendarDaysIcon class="w-5 h-5" />
          <span>Bulk Schedule</span>
        </button>
        <button
          @click="showNewSchedule = true"
          class="btn btn-primary flex items-center space-x-2"
        >
          <PlusIcon class="w-5 h-5" />
          <span>Schedule Call</span>
        </button>
      </div>
    </div>
    
    <!-- View Toggle -->
    <div class="flex space-x-2">
      <button
        v-for="view in ['day', 'week', 'month']"
        :key="view"
        @click="currentView = view"
        :class="[
          'btn btn-sm',
          currentView === view ? 'btn-primary' : 'btn-secondary'
        ]"
      >
        {{ view.charAt(0).toUpperCase() + view.slice(1) }}
      </button>
    </div>
    
    <!-- Loading State -->
    <div v-if="scheduleStore.loading" class="card">
      <div class="card-body text-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
        <p class="text-gray-500 mt-2">Loading schedule...</p>
      </div>
    </div>
    
    <!-- Calendar Navigation -->
    <div v-else class="card">
      <div class="card-body">
        <div class="flex items-center justify-between mb-4">
          <button
            @click="navigatePrevious"
            class="btn btn-secondary btn-sm"
          >
            <ChevronLeftIcon class="w-5 h-5" />
          </button>
          <h2 class="text-xl font-semibold">
            {{ calendarTitle }}
          </h2>
          <button
            @click="navigateNext"
            class="btn btn-secondary btn-sm"
          >
            <ChevronRightIcon class="w-5 h-5" />
          </button>
        </div>
        
        <!-- Day View -->
        <div v-if="currentView === 'day'" class="space-y-2">
          <div class="grid grid-cols-[auto_1fr] gap-2">
            <div
              v-for="hour in hours"
              :key="hour"
              class="contents"
            >
              <div class="text-sm text-gray-500 text-right pr-4 py-2">
                {{ formatHour(hour) }}
              </div>
              <div class="border-t border-gray-200 relative min-h-[60px]">
                <div
                  v-for="call in getCallsForHour(hour)"
                  :key="call.id"
                  @click="viewCallDetails(call)"
                  class="absolute left-0 right-0 bg-primary-100 border-l-4 border-primary-500 p-2 rounded cursor-pointer hover:bg-primary-200 mx-2"
                  :style="getCallPosition(call)"
                >
                  <div class="text-sm font-medium">{{ call.profile.name }}</div>
                  <div class="text-xs text-gray-600">{{ formatTime(call.scheduled_time) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Week View -->
        <div v-else-if="currentView === 'week'" class="overflow-x-auto">
          <div class="min-w-[800px]">
            <div class="grid grid-cols-8 gap-2">
              <div></div>
              <div
                v-for="day in weekDays"
                :key="day.date"
                class="text-center"
              >
                <div class="font-medium">{{ day.name }}</div>
                <div class="text-sm text-gray-500">{{ day.date }}</div>
              </div>
            </div>
            
            <div class="grid grid-cols-8 gap-2 mt-4">
              <template v-for="hour in hours" :key="hour">
                <div class="text-sm text-gray-500 text-right pr-2">
                  {{ formatHour(hour) }}
                </div>
                <div
                  v-for="day in weekDays"
                  :key="`${hour}-${day.date}`"
                  class="border border-gray-200 min-h-[40px] relative"
                >
                  <div
                    v-for="call in getCallsForDayHour(day.fullDate, hour)"
                    :key="call.id"
                    @click="viewCallDetails(call)"
                    class="absolute inset-1 bg-primary-100 text-xs p-1 rounded cursor-pointer hover:bg-primary-200"
                  >
                    {{ call.profile.name.split(' ')[0] }}
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
        
        <!-- Month View -->
        <div v-else-if="currentView === 'month'" class="grid grid-cols-7 gap-2">
          <div
            v-for="day in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']"
            :key="day"
            class="text-center font-medium text-sm text-gray-600 py-2"
          >
            {{ day }}
          </div>
          
          <div
            v-for="day in monthDays"
            :key="day.date"
            :class="[
              'border border-gray-200 min-h-[100px] p-2 cursor-pointer hover:bg-gray-50',
              day.isCurrentMonth ? '' : 'bg-gray-50',
              day.isToday ? 'bg-primary-50 border-primary-300' : ''
            ]"
            @click="selectDate(day.fullDate)"
          >
            <div class="text-sm font-medium mb-1">{{ day.date }}</div>
            <div class="space-y-1">
              <div
                v-for="call in getCallsForDate(day.fullDate).slice(0, 3)"
                :key="call.id"
                @click.stop="viewCallDetails(call)"
                class="text-xs bg-primary-100 px-1 py-0.5 rounded truncate hover:bg-primary-200"
              >
                {{ formatTime(call.scheduled_time) }} - {{ call.profile.name }}
              </div>
              <div
                v-if="getCallsForDate(day.fullDate).length > 3"
                class="text-xs text-gray-500"
              >
                +{{ getCallsForDate(day.fullDate).length - 3 }} more
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Upcoming Calls -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Today's Schedule -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold">Today's Calls</h3>
        </div>
        <div class="card-body">
          <div v-if="todaysSchedule.length === 0" class="text-center py-8 text-gray-500">
            No calls scheduled for today
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="call in todaysSchedule"
              :key="call.id"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div class="flex items-center">
                <div class="text-2xl mr-3">{{ call.profile.avatar || 'Y' }}</div>
                <div>
                  <div class="font-medium">{{ call.profile.name }}</div>
                  <div class="text-sm text-gray-500">{{ formatTime(call.scheduled_time) }}</div>
                </div>
              </div>
              <div class="flex space-x-2">
                <button
                  @click="initiateCall(call)"
                  class="btn btn-primary btn-sm"
                >
                  <PhoneIcon class="w-4 h-4" />
                </button>
                <button
                  @click="rescheduleCall(call)"
                  class="btn btn-secondary btn-sm"
                >
                  <ArrowPathIcon class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Upcoming Calls -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold">Upcoming Calls</h3>
        </div>
        <div class="card-body">
          <div v-if="upcomingCalls.length === 0" class="text-center py-8 text-gray-500">
            No upcoming calls scheduled
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="call in upcomingCalls"
              :key="call.id"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div class="flex items-center">
                <div class="text-2xl mr-3">{{ call.profile.avatar || 'Y' }}</div>
                <div>
                  <div class="font-medium">{{ call.profile.name }}</div>
                  <div class="text-sm text-gray-500">
                    {{ formatDate(call.scheduled_time) }} at {{ formatTime(call.scheduled_time) }}
                  </div>
                </div>
              </div>
              <button
                @click="editSchedule(call)"
                class="btn btn-secondary btn-sm"
              >
                <PencilIcon class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Schedule Call Modal -->
    <ScheduleCallModal
      v-if="showNewSchedule"
      @save="handleScheduleCall"
      @close="showNewSchedule = false"
    />
    
    <!-- Bulk Schedule Modal -->
    <BulkScheduleModal
      v-if="showBulkSchedule"
      @save="handleBulkSchedule"
      @close="showBulkSchedule = false"
    />
    
    <!-- Call Details Modal -->
    <ScheduledCallDetails
      v-if="selectedCall"
      :call="selectedCall"
      @reschedule="rescheduleCall"
      @cancel="cancelCall"
      @close="selectedCall = null"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useScheduleStore } from '@/stores/schedule'
import { useProfileStore } from '@/stores/profiles'
import { useCallStore } from '@/stores/calls'
import {
  PlusIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PhoneIcon,
  ArrowPathIcon,
  PencilIcon
} from '@heroicons/vue/24/outline'
import ScheduleCallModal from '@/components/schedule/ScheduleCallModal.vue'
import BulkScheduleModal from '@/components/schedule/BulkScheduleModal.vue'
import ScheduledCallDetails from '@/components/schedule/ScheduledCallDetails.vue'
import dayjs from 'dayjs'

const scheduleStore = useScheduleStore()
const profileStore = useProfileStore()
const callStore = useCallStore()

const currentView = ref('month')
const currentDate = ref(dayjs())
const showNewSchedule = ref(false)
const showBulkSchedule = ref(false)
const selectedCall = ref(null)

const todaysSchedule = computed(() => scheduleStore.todaysSchedule || [])
const upcomingCalls = computed(() => scheduleStore.upcomingCalls || [])
const monthSchedule = computed(() => scheduleStore.monthSchedule || [])

const hours = computed(() => {
  const hrs = []
  for (let i = 0; i < 24; i++) {
    hrs.push(i)
  }
  return hrs
})

const calendarTitle = computed(() => {
  if (currentView.value === 'day') {
    return currentDate.value.format('dddd, MMMM D, YYYY')
  } else if (currentView.value === 'week') {
    const start = currentDate.value.startOf('week')
    const end = currentDate.value.endOf('week')
    return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`
  } else {
    return currentDate.value.format('MMMM YYYY')
  }
})

const weekDays = computed(() => {
  const days = []
  const start = currentDate.value.startOf('week')
  
  for (let i = 0; i < 7; i++) {
    const day = start.add(i, 'day')
    days.push({
      name: day.format('ddd'),
      date: day.format('D'),
      fullDate: day.format('YYYY-MM-DD')
    })
  }
  
  return days
})

const monthDays = computed(() => {
  const days = []
  const start = currentDate.value.startOf('month').startOf('week')
  const currentMonth = currentDate.value.month()
  const today = dayjs().format('YYYY-MM-DD')
  
  // Generate exactly 42 days (6 weeks) to avoid infinite loops
  for (let i = 0; i < 42; i++) {
    const day = start.add(i, 'day')
    days.push({
      date: day.format('D'),
      fullDate: day.format('YYYY-MM-DD'),
      isCurrentMonth: day.month() === currentMonth,
      isToday: day.format('YYYY-MM-DD') === today
    })
  }
  
  return days
})

function formatHour(hour) {
  return dayjs().hour(hour).minute(0).format('h A')
}

function formatTime(datetime) {
  return dayjs(datetime).format('h:mm A')
}

function formatDate(datetime) {
  return dayjs(datetime).format('MMM D')
}

function navigatePrevious() {
  if (currentView.value === 'day') {
    currentDate.value = currentDate.value.subtract(1, 'day')
  } else if (currentView.value === 'week') {
    currentDate.value = currentDate.value.subtract(1, 'week')
  } else {
    currentDate.value = currentDate.value.subtract(1, 'month')
  }
  scheduleStore.setSelectedDate(currentDate.value.toDate())
}

function navigateNext() {
  if (currentView.value === 'day') {
    currentDate.value = currentDate.value.add(1, 'day')
  } else if (currentView.value === 'week') {
    currentDate.value = currentDate.value.add(1, 'week')
  } else {
    currentDate.value = currentDate.value.add(1, 'month')
  }
  scheduleStore.setSelectedDate(currentDate.value.toDate())
}

// Memoized call filtering to prevent expensive recalculations
const callsByDate = computed(() => {
  const callMap = new Map()
  monthSchedule.value.forEach(call => {
    const dateKey = dayjs(call.scheduled_time).format('YYYY-MM-DD')
    if (!callMap.has(dateKey)) {
      callMap.set(dateKey, [])
    }
    callMap.get(dateKey).push(call)
  })
  return callMap
})

function getCallsForHour(hour) {
  const dayStart = currentDate.value.format('YYYY-MM-DD')
  const dayCalls = callsByDate.value.get(dayStart) || []
  return dayCalls.filter(call => dayjs(call.scheduled_time).hour() === hour)
}

function getCallsForDayHour(date, hour) {
  const dayCalls = callsByDate.value.get(date) || []
  return dayCalls.filter(call => dayjs(call.scheduled_time).hour() === hour)
}

function getCallsForDate(date) {
  return callsByDate.value.get(date) || []
}

function getCallPosition(call) {
  const callTime = dayjs(call.scheduled_time)
  const minutes = callTime.minute()
  const top = (minutes / 60) * 100
  return `top: ${top}%; height: 30px;`
}

function selectDate(date) {
  currentDate.value = dayjs(date)
  currentView.value = 'day'
}

function viewCallDetails(call) {
  selectedCall.value = call
}

async function initiateCall(call) {
  await callStore.initiateCall(call.profile_id)
}

function rescheduleCall(call) {
  // Open reschedule modal
  console.log('Reschedule call:', call)
}

function editSchedule(call) {
  selectedCall.value = call
}

async function cancelCall(call) {
  if (confirm('Are you sure you want to cancel this scheduled call?')) {
    await scheduleStore.cancelScheduledCall(call.id)
  }
}

async function handleScheduleCall(data) {
  // Map modal fields to backend payload
  const payload = {
    profile_id: data.profile_id,
    time: data.time,
    schedule_type: data.repeat === 'none' ? 'daily' : data.repeat, // default daily if none
  }
  // For weekly, pick the weekday from selected date
  if (data.repeat === 'weekly' && data.date) {
    // day(): 0..6 (Sun..Sat) -> convert to 1..7 (Mon..Sun)
    const d = dayjs(data.date).day()
    const dow = d === 0 ? 7 : d
    payload.days_of_week = [dow]
  } else if (data.repeat === 'daily') {
    payload.days_of_week = [1,2,3,4,5,6,7]
  }
  await scheduleStore.scheduleCall(payload)
  showNewSchedule.value = false
}

async function handleBulkSchedule(data) {
  // Transform modal output to backend expectations
  const profileIds = data.profile_ids
  let schedule_type = 'daily'
  let days_of_week = [1,2,3,4,5,6,7]
  if (data.pattern === 'weekly' && data.start_date) {
    schedule_type = 'weekly'
    const d = dayjs(data.start_date).day()
    const dow = d === 0 ? 7 : d
    days_of_week = [dow] // 1..7 (Mon..Sun)
  } else if (data.pattern === 'weekdays') {
    schedule_type = 'weekly'
    days_of_week = [1,2,3,4,5]
  }
  const schedule = {
    schedule_type,
    time: data.time,
    days_of_week
  }
  await scheduleStore.bulkSchedule(profileIds, schedule)
  showBulkSchedule.value = false
}

onMounted(async () => {
  // Only load if not already loading and no data exists
  if (!profileStore.profiles.length && !profileStore.loading) {
    try { await profileStore.fetchProfiles() } catch {}
  }
  
  if (!scheduleStore.scheduledCalls.length && !scheduleStore.loading) {
    try { 
      await scheduleStore.fetchSchedule() 
    } catch (error) {
      console.log('No scheduled calls found - showing empty state')
    }
  }
})
</script>

